import { useEffect } from 'react';
import { getFromLS, isAdmin, isRootUser } from '@orientaction/utils';

const INTERACTIVE_SELECTORS = 'input, textarea, select, [contenteditable="true"]';
const STAFF_COMPANY_ROLE_IDS = [1, 2, 3, 4];

function isInteractiveElement(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.closest(INTERACTIVE_SELECTORS) !== null;
}

function getUser(): any {
  try {
    return JSON.parse(getFromLS('userInfos') || '{}');
  } catch {
    return {};
  }
}

function canSelectAndCopy(user: any): boolean {
  if (isRootUser(user) || isAdmin(user)) {
    return true;
  }
  return STAFF_COMPANY_ROLE_IDS.includes(Number(user?.company_user_role?.id));
}

function shouldProtect(): boolean {
  return !canSelectAndCopy(getUser());
}

function applyContentProtection(): () => void {
  document.body.style.setProperty('-webkit-user-select', 'none');
  document.body.style.setProperty('user-select', 'none');

  const blockClipboard = (e: Event) => {
    if (!shouldProtect() || isInteractiveElement(e.target)) return;
    e.preventDefault();
  };

  document.addEventListener('copy', blockClipboard);
  document.addEventListener('cut', blockClipboard);
  document.addEventListener('contextmenu', blockClipboard);

  const blockPrint = (e: KeyboardEvent) => {
    if (!shouldProtect()) return;
    if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
      e.preventDefault();
    }
  };

  const blockBeforePrint = () => {
    if (!shouldProtect()) return;
    document.body.classList.add('print-blocked');
  };

  const blockAfterPrint = () => {
    document.body.classList.remove('print-blocked');
  };

  document.addEventListener('keydown', blockPrint);
  window.addEventListener('beforeprint', blockBeforePrint);
  window.addEventListener('afterprint', blockAfterPrint);

  const blockDragStart = (e: DragEvent) => {
    if (!shouldProtect()) return;
    if (e.target instanceof HTMLImageElement) {
      e.preventDefault();
    }
  };

  document.addEventListener('dragstart', blockDragStart);

  const style = document.createElement('style');
  style.setAttribute('data-content-protection', '');
  style.textContent = `
    ${INTERACTIVE_SELECTORS} {
      -webkit-user-select: text !important;
      user-select: text !important;
    }
    img {
      -webkit-user-drag: none;
      user-drag: none;
      pointer-events: auto;
    }
    @media print {
      body.print-blocked *, body.print-blocked *::before, body.print-blocked *::after {
        display: none !important;
        visibility: hidden !important;
      }
      body.print-blocked::after {
        display: block !important;
        visibility: visible !important;
        content: "L'impression n'est pas autorisée sur cette plateforme.";
        position: fixed;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        color: #333;
        background: #fff;
      }
    }
  `;
  document.head.appendChild(style);

  return () => {
    document.body.style.removeProperty('-webkit-user-select');
    document.body.style.removeProperty('user-select');
    document.removeEventListener('copy', blockClipboard);
    document.removeEventListener('cut', blockClipboard);
    document.removeEventListener('contextmenu', blockClipboard);
    document.removeEventListener('keydown', blockPrint);
    window.removeEventListener('beforeprint', blockBeforePrint);
    window.removeEventListener('afterprint', blockAfterPrint);
    document.removeEventListener('dragstart', blockDragStart);
    document.body.classList.remove('print-blocked');
    style.remove();
  };
}

function subscribeToUserInfosChanges(onChange: () => void): () => void {
  const onStorage = (e: StorageEvent) => {
    if (e.key === 'userInfos' || e.key === null) {
      onChange();
    }
  };

  window.addEventListener('storage', onStorage);

  const { localStorage } = window;
  const originalSetItem = localStorage.setItem.bind(localStorage);
  const originalRemoveItem = localStorage.removeItem.bind(localStorage);
  const originalClear = localStorage.clear.bind(localStorage);

  localStorage.setItem = (key: string, value: string) => {
    originalSetItem(key, value);
    if (key === 'userInfos') onChange();
  };

  localStorage.removeItem = (key: string) => {
    originalRemoveItem(key);
    if (key === 'userInfos') onChange();
  };

  localStorage.clear = () => {
    originalClear();
    onChange();
  };

  return () => {
    window.removeEventListener('storage', onStorage);
    localStorage.setItem = originalSetItem;
    localStorage.removeItem = originalRemoveItem;
    localStorage.clear = originalClear;
  };
}

export default function useContentProtection(): void {
  useEffect(() => {
    let removeProtection: (() => void) | undefined;

    const syncProtection = () => {
      if (shouldProtect()) {
        if (!removeProtection) {
          removeProtection = applyContentProtection();
        }
        return;
      }

      removeProtection?.();
      removeProtection = undefined;
    };

    syncProtection();

    const unsubscribe = subscribeToUserInfosChanges(syncProtection);

    return () => {
      unsubscribe();
      removeProtection?.();
    };
  }, []);
}
